import { BatchJobStatus } from '@warebee/frontend/data-access-api-graphql';
import _ from 'lodash';
import React, { PropsWithChildren, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import useLoadSimulationItemSet from '../assignmentPolicy/useLoadSimulationItemSet';
import { COMPLIANCE_PROGRESS_REFRESH_INTERVAL } from '../common/constants';
import { AsyncLoadStatus } from '../common/types';
import DropdownSelector from '../components/actions/DropdownSelector';
import useLoadOrderLineSet from '../pickingPolicy/hooks/useLoadOrderLineSet';
import {
  simulationCurrent,
  simulationEffectiveItemSet,
  simulationEffectiveItemSetLoadStatus,
  simulationOrderLineSet,
  simulationOrderLineSetLoadStatus,
} from './store/simulation.state';

const SimulationRecalculateDropdown: React.FC<PropsWithChildren> = props => {
  const { t } = useTranslation('simulation');
  const sim = useRecoilValue(simulationCurrent);

  // Order Line Set state and hooks
  const orderLineSet = useRecoilValue(simulationOrderLineSet);
  const orderLineSetLoadStatus = useRecoilValue(
    simulationOrderLineSetLoadStatus,
  );
  const [loadOrderLineSet, cancelOrderLineSet] = useLoadOrderLineSet();

  // Item Set state and hooks
  const effectiveItemSet = useRecoilValue(simulationEffectiveItemSet);
  const effectiveItemSetLoadStatus = useRecoilValue(
    simulationEffectiveItemSetLoadStatus,
  );
  const [loadItemSetStatus, cancelLoadItemSetStatus] =
    useLoadSimulationItemSet();

  // Check dependencies
  const hasOrderLineSetDeps = sim.itemSet?.id && sim.orderSet?.id;
  const hasItemSetDeps =
    sim.layout?.id && sim.assignment?.id && sim.itemSet?.id && sim.orderSet?.id;

  // Order Line Set useEffect
  useEffect(() => {
    if (
      orderLineSetLoadStatus === AsyncLoadStatus.Error ||
      !hasOrderLineSetDeps
    )
      return;

    let timeoutId;
    if (_.isNil(orderLineSet)) {
      loadOrderLineSet({ simulationId: sim.id });
    }
    if (
      orderLineSet?.status === BatchJobStatus.CREATED ||
      orderLineSet?.status === BatchJobStatus.CALCULATING
    ) {
      timeoutId = setTimeout(() => {
        loadOrderLineSet({ simulationId: sim.id });
      }, COMPLIANCE_PROGRESS_REFRESH_INTERVAL);
    }

    return () => {
      clearTimeout(timeoutId);
      cancelOrderLineSet();
    };
  }, [orderLineSet, sim.itemSet?.id, sim.orderSet?.id]);

  // Item Set useEffect
  useEffect(() => {
    if (effectiveItemSetLoadStatus === AsyncLoadStatus.Error || !hasItemSetDeps)
      return;

    let timeoutId;
    if (_.isNil(effectiveItemSet)) {
      loadItemSetStatus({ simulationId: sim.id });
    }
    if (
      effectiveItemSet?.status === BatchJobStatus.CREATED ||
      effectiveItemSet?.status === BatchJobStatus.CALCULATING
    ) {
      timeoutId = setTimeout(() => {
        loadItemSetStatus({ simulationId: sim.id });
      }, COMPLIANCE_PROGRESS_REFRESH_INTERVAL);
    }

    return () => {
      clearTimeout(timeoutId);
      cancelLoadItemSetStatus();
    };
  }, [
    effectiveItemSet,
    sim.layout?.id,
    sim.assignment?.id,
    sim.itemSet?.id,
    sim.orderSet?.id,
  ]);

  // Calculate states
  const orderLineSetHasError =
    orderLineSetLoadStatus === AsyncLoadStatus.Error ||
    orderLineSet?.status === BatchJobStatus.FAILED ||
    orderLineSet?.status === BatchJobStatus.TERMINATED;

  const orderLineSetIsLoading =
    orderLineSetLoadStatus !== AsyncLoadStatus.Ok ||
    orderLineSet?.status !== BatchJobStatus.READY;

  const itemSetHasError =
    effectiveItemSetLoadStatus === AsyncLoadStatus.Error ||
    effectiveItemSet?.status === BatchJobStatus.FAILED ||
    effectiveItemSet?.status === BatchJobStatus.TERMINATED;

  const itemSetIsLoading =
    effectiveItemSetLoadStatus !== AsyncLoadStatus.Ok ||
    effectiveItemSet?.status !== BatchJobStatus.READY;

  // Create dropdown options
  const dropOptions = _.keyBy(
    [
      {
        key: 'recalcItems',
        title: t`Recalculate Items`,
        onClick: () => {
          if (!hasItemSetDeps) return;
          loadItemSetStatus({
            simulationId: sim.id,
            ...(effectiveItemSet && { forceGenerate: true }),
          });
        },
        disabled: itemSetIsLoading || !hasItemSetDeps,
        hasError: itemSetHasError,
        isLoading: itemSetIsLoading,
      },
      {
        key: 'recalcOrders',
        title: t`Recalculate Orders`,
        onClick: () => {
          if (!hasOrderLineSetDeps) return;
          loadOrderLineSet({
            simulationId: sim.id,
            ...(orderLineSet && { forceGenerate: true }),
          });
        },
        disabled: orderLineSetIsLoading || !hasOrderLineSetDeps,
        hasError: orderLineSetHasError,
        isLoading: orderLineSetIsLoading,
      },
    ],
    'key',
  );

  const anyLoading = itemSetIsLoading || orderLineSetIsLoading;
  const anyError = itemSetHasError || orderLineSetHasError;

  return (
    <DropdownSelector
      buttonTransparent
      vertical
      DropAlignRight
      onClick={e => {
        e.stopPropagation();
      }}
      value={'recalculate'}
      values={_.keys(dropOptions)}
      renderValue={key => {
        // For the button display, show overall state
        if (key === 'recalculate') {
          // Show specific loading states
          if (itemSetIsLoading && orderLineSetIsLoading) {
            return t`Analysing Items & Orders...`;
          }
          if (itemSetIsLoading) {
            return t`Analysing Items...`;
          }
          if (orderLineSetIsLoading) {
            return t`Analysing Orders...`;
          }

          // Show specific error states
          if (itemSetHasError && orderLineSetHasError) {
            return t`Re-Analyse (Items & Orders Error)`;
          }
          if (itemSetHasError) {
            return t`Re-Analyse (Items Error)`;
          }
          if (orderLineSetHasError) {
            return t`Re-Analyse (Orders Error)`;
          }

          return t`Re-Analyse`;
        }

        // For dropdown options, show state-aware titles
        if (key === 'recalcItems') {
          if (itemSetIsLoading) return t`Analysing Items...`;
          if (itemSetHasError) return t`Analysing Items (Error)`;
          return t`Re-Analyse Items`;
        }

        if (key === 'recalcOrders') {
          if (orderLineSetIsLoading) return t`Analysing Orders...`;
          if (orderLineSetHasError) return t`Analysing Orders (Error)`;
          return t`Re-Analyse Orders`;
        }

        return dropOptions[key]?.title || key;
      }}
      onChange={async (key, e) => {
        const option = dropOptions[key];
        if (option && !option.disabled && _.isFunction(option.onClick)) {
          option.onClick();
        }
      }}
      //   renderItem={key => {
      //     const option = dropOptions[key];
      //     if (!option) return null;

      //     return (
      //       <div
      //         className={cn(
      //           'flex items-center justify-between',
      //           option.disabled && 'cursor-not-allowed opacity-50',
      //           option.hasError && 'text-red-500',
      //         )}
      //       >
      //         <span>{option.title}</span>
      //         {option.isLoading && <span className="ml-2 text-xs">...</span>}
      //       </div>
      //     );
      //   }}
    />
  );
};

export default SimulationRecalculateDropdown;
